/**
 * Loga só quando a MENSAGEM muda. Repetição idêntica fica em silêncio.
 *
 * <p><b>O problema que isto resolve.</b> O console do player enchia com linhas que repetem o
 * mesmo valor a cada ciclo: a avaliação da playlist (11 linhas a cada 15s), o resultado da
 * sonda de conexão, o `hello` do SSE. Medido no player web do Renato em 2026-10-15: ~19
 * linhas a cada 15s — ≈76 por minuto — e, em regime estável, nenhuma delas mudava de valor.
 * Com esse volume, a linha que importa (um erro, uma transição) passa despercebida.</p>
 *
 * <p><b>O critério.</b> Uma linha de log se paga quando muda o que você faria a seguir.
 * Estado que não mudou não muda decisão nenhuma — então só o PRIMEIRO de uma sequência
 * idêntica aparece, e a mudança seguinte volta a aparecer com a contagem do que foi omitido.
 * Nada é perdido: a transição continua visível, que é o que se procura num diagnóstico.</p>
 */

interface Ultima {
  mensagem: string;
  repeticoes: number;
}

const ultimas = new Map<string, Ultima>();

/**
 * @param chave  identifica a FONTE do log (ex.: 'scheduler.eval'); cada fonte tem memória
 *               própria, então uma não silencia a outra
 * @param mensagem  a linha; se for igual à última daquela fonte, não loga
 * @param emitir  quem escreve de fato (console.log, otaLog.info…)
 * @returns `true` se emitiu
 */
export function logSeMudou(
  chave: string,
  mensagem: string,
  emitir: (texto: string) => void = (t) => console.log(t),
): boolean {
  const ultima = ultimas.get(chave);
  if (ultima && ultima.mensagem === mensagem) {
    ultima.repeticoes += 1;
    return false;
  }
  // Ao mudar, conta quantas iguais foram omitidas — o silêncio não esconde volume.
  const omitidas = ultima?.repeticoes ?? 0;
  ultimas.set(chave, { mensagem, repeticoes: 0 });
  emitir(omitidas > 0 ? `${mensagem}  (↑ ${omitidas}× igual)` : mensagem);
  return true;
}

/** Esquece o histórico de uma fonte (ou de todas). Usado em teste e no boot. */
export function esquecerLogs(chave?: string): void {
  if (chave === undefined) ultimas.clear();
  else ultimas.delete(chave);
}
